import axios from 'axios';
import { leaveSignedOut } from '../utils/hubSession';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add token and module header to requests (except public endpoints)
apiClient.interceptors.request.use(
  (config) => {
    // List of public endpoints that don't require authentication
    // Note: branding/config GET is public, but PUT/DELETE require auth
    const publicEndpoints = ['/auth/login', '/auth/refresh', '/demo/status', '/demo/login'];

    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some((endpoint) => config.url.includes(endpoint));

    // Special case: branding/config is only public for GET requests
    const isBrandingGet = config.url.includes('/branding/config') && config.method === 'get';

    // Only add token for non-public endpoints. Branding is public, but with a session it is the
    // person's own business's branding (each business has its own), so the token goes with it.
    if (!isPublicEndpoint || isBrandingGet) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add active module to all requests (for module-based data filtering)
    const activeModule = localStorage.getItem('activeModule');
    if (activeModule) {
      config.headers['X-Module'] = activeModule;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh promise to prevent race condition
let refreshTokenPromise = null;

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    // Skip for login endpoint as 401 means invalid credentials there
    // Skip for /auth/me and /auth/refresh to prevent infinite loops
    // Skip for demo endpoints as they are public
    // Skip for branding/config GET as it's public (PUT/DELETE require auth)
    const isBrandingGet =
      originalRequest.url.includes('/branding/config') && originalRequest.method === 'get';
    const isPublicEndpoint =
      originalRequest.url.includes('/auth/login') ||
      originalRequest.url.includes('/auth/refresh') ||
      originalRequest.url.includes('/demo/status') ||
      originalRequest.url.includes('/demo/login') ||
      isBrandingGet;
    // Access turned off while signed in (deactivated, locked or deleted here, or removed from
    // the business in One Manager): the session is over, whatever the token says
    if (error.response?.status === 403 && error.response.data?.code === 'ACCOUNT_INACTIVE') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      if (!leaveSignedOut('closed') && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true;

      // Sent with an older session's token while a newer sign-in has stored its own (e.g. a
      // tab left open, then a fresh sign-in from One Manager): retry with the new one, and never
      // let the old token end the new session.
      const current = localStorage.getItem('accessToken');
      const sentWith = String(originalRequest.headers?.Authorization || '').replace(/^Bearer /, '');
      if (current && sentWith && sentWith !== current) {
        originalRequest.headers.Authorization = `Bearer ${current}`;
        return apiClient(originalRequest);
      }

      // The sign-in and session-ended pages deal with their own outcome
      if (/^\/(sso|session-ended)(\/|$)/.test(window.location.pathname)) {
        return Promise.reject(error);
      }

      try {
        // If a refresh is already in progress, wait for it
        if (refreshTokenPromise) {
          await refreshTokenPromise;
          // Retry original request with the new token
          const token = localStorage.getItem('accessToken');
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Create refresh promise to prevent concurrent refreshes
        refreshTokenPromise = axios
          .post(`${API_URL}/auth/refresh`, {
            refreshToken
          })
          .then((response) => {
            const { accessToken } = response.data.data;

            // Store new access token
            localStorage.setItem('accessToken', accessToken);

            return accessToken;
          })
          .finally(() => {
            // Clear the promise after completion
            refreshTokenPromise = null;
          });

        // Wait for token refresh
        const accessToken = await refreshTokenPromise;

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        // Hub users see why on the session-ended screen (naming the refused request). Otherwise,
        // only redirect if not already on the login page, to prevent an infinite loop
        const call = `${String(originalRequest.method || 'get').toUpperCase()} ${originalRequest.url}`;
        // The API says when the reason was inactivity (Settings > Security timeout)
        const reason = error.response?.data?.session_expired ? 'inactive' : 'expired';
        if (!leaveSignedOut(reason, { call }) && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
